import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'

/* ────────────────────────────────────────────────────────────────
 * Types
 * ──────────────────────────────────────────────────────────────── */

export enum NotificationType {
    INFO = 'info',
    SUCCESS = 'success',
    WARNING = 'warning',
    ERROR = 'error',
}

export interface Notification {
    id: number
    title: string
    message: string
    notification_type: NotificationType
    is_read: boolean
    is_archived?: boolean
    link?: string
    created_at: string
}

/* ────────────────────────────────────────────────────────────────
 * Helpers
 * ──────────────────────────────────────────────────────────────── */

export function getNotificationTypeColor(type: NotificationType): string {
    const colors: Record<NotificationType, string> = {
        [NotificationType.INFO]: '#3b82f6',
        [NotificationType.SUCCESS]: '#16a34a',
        [NotificationType.WARNING]: '#f59e0b',
        [NotificationType.ERROR]: '#dc2626',
    }
    return colors[type] ?? '#6b7280'
}

/* ────────────────────────────────────────────────────────────────
 * Query: Get Notifications
 * GET /api/notifications
 * ──────────────────────────────────────────────────────────────── */

interface UseNotificationsParams {
    unread_only?: boolean
    notification_type?: NotificationType
    limit?: number
    offset?: number
}

export const useNotifications = (params?: UseNotificationsParams) =>
    useQuery<Notification[]>({
        queryKey: ['notifications', params],
        queryFn: async () => {
            const { data } = await apiClient.get<Notification[]>('/api/notifications', {
                params,
            })
            return data
        },
    })

/* ────────────────────────────────────────────────────────────────
 * Query: Unread Count
 * Derived from GET /api/notifications?unread_only=true
 * ──────────────────────────────────────────────────────────────── */

export const useUnreadCount = () =>
    useQuery<number>({
        queryKey: ['notifications', 'unread', 'count'],
        queryFn: async () => {
            const { data } = await apiClient.get<Notification[]>('/api/notifications', {
                params: { unread_only: true, limit: 100 },
            })
            return Array.isArray(data) ? data.length : 0
        },
        refetchInterval: 60_000,
    })

/* ────────────────────────────────────────────────────────────────
 * Mutation: Update Notification
 * PATCH /api/notifications/{id}
 * ──────────────────────────────────────────────────────────────── */

interface UpdateNotificationPayload {
    notification_id: number
    update: Partial<Pick<Notification, 'is_read' | 'is_archived'>>
}

export const useUpdateNotification = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ notification_id, update }: UpdateNotificationPayload) => {
            const { data } = await apiClient.patch(
                `/api/notifications/${notification_id}`,
                update
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
        },
    })
}

/* ────────────────────────────────────────────────────────────────
 * Mutation: Delete Notification
 * DELETE /api/notifications/{id}
 * ──────────────────────────────────────────────────────────────── */

export const useDeleteNotification = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id: number) => {
            await apiClient.delete(`/api/notifications/${id}`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
        },
    })
}

/* ────────────────────────────────────────────────────────────────
 * Mutation: Mark All as Read
 * POST /api/notifications/mark-all-read
 * ──────────────────────────────────────────────────────────────── */

export const useMarkAllRead = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async () => {
            const { data } = await apiClient.post('/api/notifications/mark-all-read')
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
        },
    })
}